import React, { useState, useEffect } from 'react';
import { SupplierCard } from './SupplierCard/SupplierCard';
import { SupplierCard_SupplierProduct } from './SupplierCard/SupplierCard';
import Button from '../common/Button';
import { gql, useQuery } from '@apollo/client';
import { SupplierProduct, SupplierSelector_GetSupplierProductsQuery, SupplierSelector_GetSupplierProductsQueryVariables } from '../../../codegen/graphql';

const GET_SUPPLIER_PRODUCTS = gql`
  query SupplierSelector_GetSupplierProducts($adId: ID!) {
    supplierProducts(adId: $adId) {
      nodes {
        ...SupplierCard_SupplierProduct
      }
    }
  }
  ${SupplierCard_SupplierProduct}
`;

interface SupplierSelectorProps {
  adId: string;
  title: React.ReactNode;
  initialSelectedVariations: Array<{
    sku: string;
    supplierProductId: string;
  }>;
  onComplete: (selectedVariations: Array<{
    sku: string;
    supplierProductId: string;
  }>) => void;
  onCancel: () => void;
}

export const SupplierSelector: React.FC<SupplierSelectorProps> = ({
  adId,
  title,
  initialSelectedVariations = [],
  onComplete,
  onCancel
}) => {
  const [selectedVariations, setSelectedVariations] = useState<Array<{
    sku: string;
    supplierProductId: string;
  }>>(initialSelectedVariations);

  const { data, loading, error } = useQuery<
    SupplierSelector_GetSupplierProductsQuery,
    SupplierSelector_GetSupplierProductsQueryVariables
  >(
    GET_SUPPLIER_PRODUCTS,
    {
    variables: { adId },
    fetchPolicy: 'network-only'
  });

  const handleVariationToggle = (variation: {
    sku: string;
    supplierProductId: string;
  }) => {
    setSelectedVariations(prev => {
      // Check if this variation is already selected
      const isSelected = prev.some(
        v => v.sku === variation.sku && 
             v.supplierProductId === variation.supplierProductId
      );

      if (isSelected) {
        // Remove it if already selected
        return prev.filter(
          v => !(v.sku === variation.sku && 
                v.supplierProductId === variation.supplierProductId)
        );
      } else {
        // Add it if not selected
        return [...prev, variation];
      }
    });
  };

  const handleComplete = () => {
    onComplete(selectedVariations);
  };

  // Custom updateAd function for SupplierCard that doesn't actually update the ad
  // but allows the component to work without errors
  const dummyUpdateAd = () => {};

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-start overflow-y-auto p-4">
      <div className="bg-gray-100 rounded-lg w-full max-w-6xl max-h-full flex flex-col">
        <div className="p-4 bg-white rounded-t-lg border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">{title}</h2>
          <div className="flex space-x-2">
            <Button onClick={onCancel}>
              Annuler
            </Button>
            <Button onClick={handleComplete} preset='primary'>
              Confirmer ({selectedVariations.length} sélectionnées)
            </Button>
          </div>
        </div>
        
        <div className="overflow-y-auto p-4 grow">
          {loading && (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-sm">
              Erreur lors du chargement des produits: {error.message}
            </div>
          )}
          
          {data?.supplierProducts?.nodes && data.supplierProducts.nodes.length === 0 && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-sm">
              Aucun produit fournisseur trouvé pour cette annonce.
            </div>
          )}
          
          {data?.supplierProducts?.nodes && data.supplierProducts.nodes.map((product) => (
            <div key={`${product.id}-${product.supplierName}`} className="mb-6">
              <SupplierCard 
                adId={adId} 
                supplierProduct={product} 
                updateAd={dummyUpdateAd}
                selectable={true}
                selectedVariations={selectedVariations}
                onVariationToggle={handleVariationToggle}
              />
            </div>
          ))}
        </div>
        
        <div className="p-4 bg-white rounded-b-lg border-t border-gray-200 flex justify-between">
          <Button onClick={onCancel}>
            Annuler
          </Button>
          <Button onClick={handleComplete} preset='primary'>
            Confirmer ({selectedVariations.length} sélectionnées)
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SupplierSelector;
